import React, { useEffect, useState } from 'react';
import {
  Badge,
  NavLink,
  PopoverBody,
  PopoverHeader,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { toast } from 'react-toastify';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import EyeIcon from 'mdi-react/EyeIcon';
import { useHistory } from 'react-router-dom';
import t from '../../../util/translation/translation';
import Pagination from '../pagination/Pagination';
import Loading from '../custom/Loading';
import Api from '../../../util/api';
import FormCard from '../custom/FormCard';
import SquareIconButton from '../custom/SquareIconButton';
import { formatDateTime } from '../../../util/date-and-time';
import { getMessageType, getOriginLink, getTitleAndBody } from './utils';
import useModal from '../../hooks/useModal';
import DeleteModal from '../custom/DeleteModal';
import { scheduleIntervals } from '../../../containers/messages/components/MessagesForm';

const SentMessages = () => {
  const history = useHistory();
  const [order, setOrder] = useState({
    page: 1,
    size: 10,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [handleModal, visible, deleteID] = useModal();
  const getScheduledMessages = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const { data: scheduledMessagesRes } =
        await Api.scheduleMessages.getScheduleMessages(
          page,
          pageSize,
          'Scheduled'
        );
      const filledData = await Promise.all(
        scheduledMessagesRes.data.map((message) =>
          getOriginLink(message)
            .then((originalLinkResult) => ({
              ...message,
              originalLinkResult,
            }))
            .catch(() => ({
              ...message,
              originalLinkResult: { title: null, link: null },
            }))
        )
      );
      setMessages(filledData);
      setLoading(false);
      setTotal(scheduledMessagesRes.total);
    } catch (err) {
      toast.error(err || 'error');
      setLoading(false);
    }
  };
  const deleteScheduledMessage = async () => {
    setLoading(true);
    try {
      await Api.scheduleMessages.deleteMessage(deleteID);
      toast.success(t('shared.deleteSuccessful'));
    } catch (err) {
      toast.error(err);
    } finally {
      handleModal();
      setOrder({ page: 1, size: 10 });
      getScheduledMessages();
    }
  };

  const onChangePageSystem = (pager) => {
    if (order.page !== pager.currentPage || order.size !== pager.pageSize) {
      getScheduledMessages(pager.currentPage, pager.pageSize);
      setOrder({
        page: pager.currentPage,
        size: pager.pageSize,
      });
    }
  };
  useEffect(() => {
    getScheduledMessages();
  }, []);
  const getScheduleInterval = (scheduleIntervalMs) => {
    const scheduleInterval = scheduleIntervals.find(
      (interval) => interval.value === scheduleIntervalMs
    );
    return scheduleInterval
      ? scheduleInterval.label
      : t('ScheduledMessagesPage.scheduleInterval-dontRepeat');
  };
  const renderSentMessages = () => {
    return messages
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((message) => {
        let typeColor;
        switch (getMessageType(message)) {
          case 'push':
            typeColor = 'success';
            break;
          case 'sms':
            typeColor = 'danger';
            break;
          case 'email':
            typeColor = 'warning';
            break;
          default:
            typeColor = 'default';
        }
        const { title, link } = message.originalLinkResult;
        const { messageTitle, messageBody } = getTitleAndBody(message);
        return (
          <tr key={`renderScheduledMessages-${message.id}`}>
            <td>{messageTitle}</td>
            <td>
              {message?.date
                ? formatDateTime(message?.date)
                : message?.createdAt
                  ? formatDateTime(message?.createdAt)
                  : '-- --'}
            </td>
            <td>{getScheduleInterval(message?.payload?.scheduleIntervalMs)}</td>
            <td>
              <Badge color={typeColor}>{getMessageType(message)}</Badge>
            </td>
            <td>
              <NavLink href={link} disabled={!link}>
                {title}
              </NavLink>
            </td>
            <td>
              <div className="icon-actions">
                <SquareIconButton
                  icon={<EyeIcon />}
                  onClick={() => {}}
                  id={`messageBody${message.id}`}
                />
                <UncontrolledPopover
                  trigger="legacy"
                  placement="left"
                  target={`messageBody${message.id}`}
                  style={{ maxWidth: 420 }}
                >
                  <PopoverHeader style={{ padding: '12px 16px', fontSize: 16 }}>
                    <span style={{ wordBreak: 'break-word' }}>
                      {messageTitle}
                    </span>
                  </PopoverHeader>
                  <PopoverBody style={{ padding: 16 }}>
                    <small className="text-muted d-block mb-2">
                      {t('shared.description')}
                    </small>
                    <div
                      style={{
                        maxHeight: 260,
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      {messageBody || '-'}
                    </div>
                  </PopoverBody>
                </UncontrolledPopover>
                <SquareIconButton
                  icon={<EditOutlineIcon />}
                  onClick={() =>
                    history.push({
                      pathname: `/messages/edit/${message.id}`,
                      state: message,
                    })
                  }
                  title={t('shared.edit')}
                  id={`editBtn-${message.id}`}
                />
                <SquareIconButton
                  icon={<DeleteOutlineIcon />}
                  onClick={() => handleModal(message.id)}
                  title={t('shared.delete')}
                  tone="danger"
                  id={`deleteBtn-${message.id}`}
                />
              </div>
            </td>
          </tr>
        );
      });
  };
  return (
    <div className="flex-1">
      <DeleteModal
        visible={visible}
        type={t('ScheduledMessagesPage.scheduledMessages')}
        handleModal={handleModal}
        modalAction={deleteScheduledMessage}
      />
      <Loading loading={loading} />
      <FormCard
        title={t('ScheduledMessagesPage.scheduledMessages')}
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy">
          <thead>
            <tr>
              <th>{t('shared.title')}</th>
              <th>{t('shared.publication')}</th>
              <th>{t('ScheduledMessagesPage.scheduleInterval')}</th>
              <th>{t('shared.type')}</th>
              <th>{t('shared.origin')}</th>
              <th />
            </tr>
          </thead>
          <tbody>{renderSentMessages()}</tbody>
        </Table>
      </FormCard>
      <Pagination
        items={total}
        onChangePage={onChangePageSystem}
        initialPage={0}
      />
    </div>
  );
};

export default SentMessages;
